/* client/src/components/Investments.tsx */
import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Box,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Tabs,
  Tab,
  Grid
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  TrendingUp as ReturnIcon,
  SwapHoriz as TransferIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { 
  apiGetInvestments, 
  apiGetInvestmentReturns, 
  apiAddInvestment, 
  apiAddInvestmentReturn, 
  apiUpdateInvestment, 
  apiDeleteInvestment,
  apiGetAvailableInvestments,
  apiTransferToCapital,
  apiGetCapitalBalances,
  apiGetTransferHistory,
  apiGetInvestmentTotalsByBusinessUnit
} from '../services/apiService';
import { apiGetBusinessUnits } from '../services/apiService';

interface Investment {
  id: number;
  date: string;
  business_unit_id: number;
  business_unit_name: string;
  amount: number;
  description: string;
  type_id: number | null;
  type_name: string | null;
  status: string;
  expected_return: number | null;
  return_date: string | null;
  created_at?: string;
}

interface InvestmentReturn {
  id: number;
  investment_id: number;
  return_date: string;
  return_amount: number;
  description: string;
  created_at?: string;
}

interface BusinessUnit {
  id: number;
  name: string;
}

interface InvestmentTotalsByBusinessUnit {
  business_unit_id: number;
  business_unit_name: string;
  total_investment: number;
  total_transferred: number;
  net_investment: number;
}

interface AvailableInvestment {
  id: number;
  amount: number;
  description: string;
  date: string;
  business_unit_name: string;
}

interface CapitalBalance {
  business_unit_id: number;
  business_unit_name: string;
  balance: number;
  cash_balance: number;
  currency: string;
  updated_at: string;
}

interface CapitalTransfer {
  id: number;
  investment_id: number;
  business_unit_id: number;
  business_unit_name: string;
  amount: number;
  description: string;
  transfer_type: string;
  created_at: string;
  investment_description?: string;
}

function Investments() {
  const [investments, setInvestments] = useState<Investment[]>([]);
  const [investmentReturns, setInvestmentReturns] = useState<InvestmentReturn[]>([]);
  const [availableInvestments, setAvailableInvestments] = useState<AvailableInvestment[]>([]);
  const [capitalBalances, setCapitalBalances] = useState<CapitalBalance[]>([]);
  const [transferHistory, setTransferHistory] = useState<CapitalTransfer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [businessUnits, setBusinessUnits] = useState<BusinessUnit[]>([]);
  const [editingInvestment, setEditingInvestment] = useState<Investment | null>(null);
  const [investmentTotals, setInvestmentTotals] = useState<InvestmentTotalsByBusinessUnit[]>([]);
  const [tabValue, setTabValue] = useState(0);
  
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    business_unit_id: '',
    amount: '',
    description: '',
    type_id: '',
    expected_return: '',
    return_date: ''
  });

  const [returnFormData, setReturnFormData] = useState({
    investment_id: '',
    return_date: new Date().toISOString().split('T')[0],
    return_amount: '',
    description: ''
  });

  const fetchInvestmentTotals = async () => {
    try {
      const data = await apiGetInvestmentTotalsByBusinessUnit();
      setInvestmentTotals(data);
    } catch (err: any) {
      console.error('Failed to fetch investment totals:', err);
    }
  };

  const [transferFormData, setTransferFormData] = useState({
    investment_id: '',
    amount: '',
    description: ''
  });

  useEffect(() => {
    fetchInvestments();
    fetchInvestmentReturns();
    fetchBusinessUnits();
    fetchAvailableInvestments();
    fetchCapitalBalances();
    fetchTransferHistory();
    fetchInvestmentTotals();
  }, []);

  const fetchInvestments = async () => {
    try {
      setLoading(true);
      const data = await apiGetInvestments();
      setInvestments(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch investments');
    } finally {
      setLoading(false);
    }
  };

  const fetchInvestmentReturns = async () => {
    try {
      const data = await apiGetInvestmentReturns();
      setInvestmentReturns(data);
    } catch (err: any) {
      console.error('Failed to fetch investment returns:', err);
    }
  };

  const fetchBusinessUnits = async () => {
    try {
      const data = await apiGetBusinessUnits();
      setBusinessUnits(data);
    } catch (err: any) {
      console.error('Failed to fetch business units:', err);
      setBusinessUnits([]);
    }
  };

  const fetchAvailableInvestments = async () => {
    try {
      const data = await apiGetAvailableInvestments();
      setAvailableInvestments(data);
    } catch (err: any) {
      console.error('Failed to fetch available investments:', err);
    }
  };

  const fetchCapitalBalances = async () => {
    try {
      const data = await apiGetCapitalBalances();
      setCapitalBalances(data);
    } catch (err: any) {
      console.error('Failed to fetch capital balances:', err);
    }
  };

  const fetchTransferHistory = async () => {
    try {
      const data = await apiGetTransferHistory();
      setTransferHistory(data);
    } catch (err: any) {
      console.error('Failed to fetch transfer history:', err);
    }
  };

  const handleOpen = (investment: Investment | null = null) => {
    if (investment) {
      setEditingInvestment(investment);
      setFormData({
        date: investment.date,
        business_unit_id: investment.business_unit_id.toString(),
        amount: investment.amount.toString(),
        description: investment.description,
        type_id: investment.type_id?.toString() || '',
        expected_return: investment.expected_return?.toString() || '',
        return_date: investment.return_date || ''
      });
    } else {
      setFormData({
        date: new Date().toISOString().split('T')[0],
        business_unit_id: '',
        amount: '',
        description: '',
        type_id: '',
        expected_return: '',
        return_date: ''
      });
    }
    setOpen(true);
  };

  const handleReturnOpen = (investment: Investment) => {
    setReturnFormData({
      investment_id: investment.id.toString(),
      return_date: new Date().toISOString().split('T')[0],
      return_amount: '',
      description: ''
    });
    setReturnOpen(true);
  };

  const handleTransferOpen = () => {
    setTransferFormData({
      investment_id: '',
      amount: '',
      description: ''
    });
    setTransferOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingInvestment(null);
  };

  const handleReturnClose = () => {
    setReturnOpen(false);
  };

  const handleTransferClose = () => {
    setTransferOpen(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleReturnChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setReturnFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTransferChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setTransferFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const typeMap: { [key: string]: number } = {
        'capital_injection': 1,
        'loan': 2,
        'equipment': 3,
        'real_estate': 4,
        'stocks': 5,
        'other': 6
      };

      const investmentData = {
        ...formData,
        business_unit_id: parseInt(formData.business_unit_id),
        amount: parseFloat(formData.amount),
        type_id: formData.type_id ? typeMap[formData.type_id] || null : null,
        expected_return: formData.expected_return ? parseFloat(formData.expected_return) : null,
        return_date: formData.return_date || null
      };

      if (editingInvestment) {
        await apiUpdateInvestment(editingInvestment.id, investmentData);
      } else {
        await apiAddInvestment(investmentData);
      }
      
      fetchInvestments();
      fetchAvailableInvestments();
      fetchInvestmentTotals();
      handleClose();
    } catch (err: any) {
      setError(err.message || `Failed to ${editingInvestment ? 'update' : 'add'} investment`);
    }
  };

  const handleReturnSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const returnData = {
        ...returnFormData,
        investment_id: parseInt(returnFormData.investment_id),
        return_amount: parseFloat(returnFormData.return_amount)
      };

      await apiAddInvestmentReturn(returnData);
      
      fetchInvestmentReturns();
      handleReturnClose();
    } catch (err: any) {
      setError(err.message || 'Failed to add investment return');
    }
  };

  const handleTransferSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const selectedInvestment = availableInvestments.find(
        inv => inv.id === parseInt(transferFormData.investment_id)
      );

      if (!selectedInvestment) {
        setError('Invalid investment selected');
        return;
      }

      const transferAmount = parseFloat(transferFormData.amount);
      
      if (transferAmount > selectedInvestment.amount) {
        setError(`Transfer amount exceeds available investment balance (${selectedInvestment.amount.toFixed(2)} AED)`);
        return;
      }

      await apiTransferToCapital({
        investment_id: parseInt(transferFormData.investment_id),
        amount: transferAmount,
        description: transferFormData.description
      });

      // Refresh all data
      fetchInvestments();
      fetchAvailableInvestments();
      fetchCapitalBalances();
      fetchTransferHistory();
      fetchInvestmentTotals();
      
      setTransferOpen(false);
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to transfer funds to capital');
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this investment?')) {
      try {
        await apiDeleteInvestment(id);
        fetchInvestments();
        fetchAvailableInvestments();
        fetchInvestmentTotals();
      } catch (err: any) {
        setError(err.message || 'Failed to delete investment');
      }
    }
  };

  if (loading) return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
      <CircularProgress />
    </Container>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Investments Management</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
            Add Investment
          </Button>
          <Button 
            variant="outlined" 
            color="secondary"
            startIcon={<TransferIcon />}
            onClick={handleTransferOpen}
            disabled={availableInvestments.length === 0}
          >
            Transfer to Capital
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<HistoryIcon />}
            onClick={() => setHistoryOpen(true)}
          >
            View History
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Investment & Capital Overview */}
<Box sx={{ mt: 3, mb: 3 }}>
  <Typography variant="h6" gutterBottom>Investment & Capital Overview</Typography>
  <Grid container spacing={2}>
    {capitalBalances.map((balance) => {
      const investmentTotal = investmentTotals.find(
        total => total.business_unit_id === balance.business_unit_id
      );
      
      // Helper function to format numbers with thousand separators
      const formatNumber = (num: number) => {
        return new Intl.NumberFormat('en-US').format(num);
      };
      
      return (
        <Grid item xs={12} sm={6} md={4} key={balance.business_unit_id}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              {balance.business_unit_name}
            </Typography>
            
            {/* Available Capital - FIXED: use balance instead of cash_balance */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="textSecondary">
                Available Capital:
              </Typography>
              <Typography variant="h6" color="success.main">
                د.{formatNumber(Number(balance.balance) || 0)}
              </Typography>
            </Box>
            
            {/* Transferred to Capital */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="textSecondary">
                Transferred to Capital:
              </Typography>
              <Typography variant="body1" color="secondary">
                د.{formatNumber(investmentTotal?.total_transferred || 0)}
              </Typography>
            </Box>
            
            {/* Net Investment Balance */}
            <Box>
              <Typography variant="body2" color="textSecondary">
                Net Investment Balance:
              </Typography>
              <Typography variant="h6" color="info.main">
                د.{formatNumber(investmentTotal?.net_investment || 0)}
              </Typography>
            </Box>
          </Paper>
        </Grid>
      );
    })}
  </Grid>
</Box>

      <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
        <Tab label="Investments" />
        <Tab label="Returns" />
      </Tabs>

      {tabValue === 0 && (
  <TableContainer component={Paper}>
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>Date</TableCell>
          <TableCell>Business Unit</TableCell>
          <TableCell>Amount</TableCell>
          <TableCell>Type</TableCell>
          <TableCell>Status</TableCell>
          <TableCell>Expected Return</TableCell>
          <TableCell>Description</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {investments.map((investment) => (
          <TableRow key={investment.id}>
            <TableCell>{new Date(investment.date).toLocaleDateString()}</TableCell>
            <TableCell>{investment.business_unit_name}</TableCell>
            <TableCell>د.{investment.amount.toFixed(2)}</TableCell>
            <TableCell>
              <Chip 
                label={investment.type_name?.replace('_', ' ') || investment.type_id || 'N/A'} 
                color="primary" 
                size="small" 
              />
            </TableCell>
            <TableCell>
              <Chip 
                label={investment.status} 
                color={investment.status === 'active' ? 'success' : 'default'} 
                size="small" 
              />
            </TableCell>
            <TableCell>
              {investment.expected_return ? `${investment.expected_return}%` : 'N/A'}
            </TableCell>
            <TableCell>{investment.description}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
    {investments.length === 0 && !loading && (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="textSecondary">No investments found</Typography>
      </Box>
    )}
  </TableContainer>
)}
      {tabValue === 1 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Return Date</TableCell>
                <TableCell>Investment ID</TableCell>
                <TableCell>Return Amount</TableCell>
                <TableCell>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {investmentReturns.map((returnItem) => (
                <TableRow key={returnItem.id}>
                  <TableCell>{new Date(returnItem.return_date).toLocaleDateString()}</TableCell>
                  <TableCell>#{returnItem.investment_id}</TableCell>
                  <TableCell>د.{returnItem.return_amount.toFixed(2)}</TableCell>
                  <TableCell>{returnItem.description}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {investmentReturns.length === 0 && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="textSecondary">No investment returns found</Typography>
            </Box>
          )}
        </TableContainer>
      )}

      {/* Investment Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingInvestment ? 'Edit Investment' : 'Add New Investment'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              margin="dense"
              name="date"
              label="Date"
              type="date"
              fullWidth
              variant="outlined"
              value={formData.date}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              required
            />
            <TextField
              margin="dense"
              name="business_unit_id"
              label="Business Unit"
              select
              fullWidth
              variant="outlined"
              value={formData.business_unit_id}
              onChange={handleChange}
              required
            >
              {businessUnits.map((unit) => (
                <MenuItem key={unit.id} value={unit.id}>
                  {unit.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              margin="dense"
              name="amount"
              label="Amount"
              type="number"
              fullWidth
              variant="outlined"
              value={formData.amount}
              onChange={handleChange}
              inputProps={{ step: "0.01", min: "0" }}
              required
            />
            <TextField
              margin="dense"
              name="type_id" 
              label="Type"
              select
              fullWidth
              variant="outlined"
              value={formData.type_id}
              onChange={handleChange}
              required
            >
              <MenuItem value="capital_injection">Capital Injection</MenuItem>
              <MenuItem value="loan">Loan</MenuItem>
              <MenuItem value="equipment">Equipment</MenuItem>
              <MenuItem value="real_estate">Real Estate</MenuItem>
              <MenuItem value="stocks">Stocks</MenuItem>
              <MenuItem value="other">General</MenuItem>
            </TextField>
            <TextField
              margin="dense"
              name="expected_return"
              label="Expected Return (%)"
              type="number"
              fullWidth
              variant="outlined"
              value={formData.expected_return}
              onChange={handleChange}
              inputProps={{ step: "0.01", min: "0" }}
            />
            <TextField
              margin="dense"
              name="return_date"
              label="Expected Return Date"
              type="date"
              fullWidth
              variant="outlined"
              value={formData.return_date}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              margin="dense"
              name="description"
              label="Description"
              fullWidth
              variant="outlined"
              value={formData.description}
              onChange={handleChange}
              multiline
              rows={3}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingInvestment ? 'Update' : 'Add'} Investment
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Return Dialog */}
      <Dialog open={returnOpen} onClose={handleReturnClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Add Investment Return
        </DialogTitle>
        <form onSubmit={handleReturnSubmit}>
          <DialogContent>
            <TextField
              margin="dense"
              name="return_date"
              label="Return Date"
              type="date"
              fullWidth
              variant="outlined"
              value={returnFormData.return_date}
              onChange={handleReturnChange}
              InputLabelProps={{ shrink: true }}
              required
            />
            <TextField
              margin="dense"
              name="return_amount"
              label="Return Amount"
              type="number"
              fullWidth
              variant="outlined"
              value={returnFormData.return_amount}
              onChange={handleReturnChange}
              inputProps={{ step: "0.01", min: "0" }}
              required
            />
            <TextField
              margin="dense"
              name="description"
              label="Description"
              fullWidth
              variant="outlined"
              value={returnFormData.description}
              onChange={handleReturnChange}
              multiline
              rows={3}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleReturnClose}>Cancel</Button>
            <Button type="submit" variant="contained">
              Add Return
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Transfer to Capital Dialog */}
      <Dialog open={transferOpen} onClose={handleTransferClose} maxWidth="sm" fullWidth>
        <DialogTitle>Transfer Funds to Capital</DialogTitle>
        <form onSubmit={handleTransferSubmit}>
          <DialogContent>
            <TextField
              margin="dense"
              name="investment_id"
              label="Select Investment"
              select
              fullWidth
              variant="outlined"
              value={transferFormData.investment_id}
              onChange={handleTransferChange}
              required
            >
              {availableInvestments.map((investment) => (
                <MenuItem key={investment.id} value={investment.id}>
                  {investment.business_unit_name} - د.{investment.amount.toFixed(2)} - {investment.description}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              margin="dense"
              name="amount"
              label="Transfer Amount (AED)"
              type="number"
              fullWidth
              variant="outlined"
              value={transferFormData.amount}
              onChange={handleTransferChange}
              inputProps={{ step: "0.01", min: "0" }}
              required
            />
            <TextField
              margin="dense"
              name="description"
              label="Transfer Description"
              fullWidth
              variant="outlined"
              value={transferFormData.description}
              onChange={handleTransferChange}
              multiline
              rows={3}
              required
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleTransferClose}>Cancel</Button>
            <Button type="submit" variant="contained" color="secondary">
              Transfer to Capital
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Transfer History Dialog */}
      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Capital Transfer History</DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Business Unit</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Type</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transferHistory.map((transfer) => (
                  <TableRow key={transfer.id}>
                    <TableCell>{new Date(transfer.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>{transfer.business_unit_name}</TableCell>
                    <TableCell>د.{(Number(transfer.amount) || 0).toFixed(2)}</TableCell>
                    <TableCell>{transfer.description}</TableCell>
                    <TableCell>
                      <Chip 
                        label={transfer.transfer_type.replace(/_/g, ' ')} 
                        size="small" 
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {transferHistory.length === 0 && (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="textSecondary">No transfer history found</Typography>
              </Box>
            )}
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Investments;